// SPDX-License-Identifier: Elastic-2.0
/**
 * useRoutines — TanStack Query hooks for the F-300 routines surface (T-026).
 *
 * Surfaces:
 *  - `useRoutines()` — list query the `/routines` page subscribes to.
 *    5 min stale time (per `queryClient.ts` rationale: routines are
 *    rarely fetched, post-load via the Routines view) + window-focus
 *    refetch from the global default.
 *  - `useTriggerRoutine()` — mutation for the per-card "Run now" button.
 *    On success: invalidates `["routines"]` so any optimistic state from
 *    the F-300 WebSocket `routine_executed` frame is reconciled.
 *  - `useDeleteRoutine()` — mutation for the kebab → Delete flow. The
 *    F-300 OpenAPI surface doesn't yet expose a `DELETE /routines/{id}`
 *    method (T-015 stubs return 501), so this hook calls the endpoint
 *    via the generated `request()` core; the error surfaces back to the
 *    UI as a failure state. On success it invalidates the list.
 *  - `useToggleRoutine()` — mutation for the kebab → Disable/Enable
 *    toggle. Same OpenAPI-surface caveat as delete; we call
 *    `PATCH /routines/{id}` with `{ active }` against the gateway. UI
 *    surfaces a banner on failure.
 *
 * Cache key shape (loose-public so siblings can read / WS bridge can
 * invalidate from outside this module):
 *   - `["routines"]` → RoutineListResponse
 *
 * `useApproveSuggestion` (T-022) already invalidates `["routines"]` on
 * success — accepting a suggestion creates a new routine, and the list
 * page picks it up without an extra refetch trigger here.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { OpenAPI } from "@/api/gateway/core/OpenAPI";
import { request as gatewayRequest } from "@/api/gateway/core/request";
import { RoutinesService } from "@/api/gateway/services/RoutinesService";
import type { Routine } from "@/api/gateway/models/Routine";
import type { RoutineListResponse } from "@/api/gateway/models/RoutineListResponse";
import type { RoutineTriggerResponse } from "@/api/gateway/models/RoutineTriggerResponse";

/** Shared TanStack query key — exported so tests + WS invalidation agree. */
export const ROUTINES_QUERY_KEY = ["routines"] as const;

/**
 * List every routine the user can see. The Routines page renders this
 * sorted active-first then alphabetical (sort lives in the page, not
 * the hook — same pattern as `groupDevicesByRoom`).
 */
export function useRoutines(): UseQueryResult<RoutineListResponse, Error> {
  return useQuery<RoutineListResponse, Error>({
    queryKey: ROUTINES_QUERY_KEY,
    queryFn: () => RoutinesService.listRoutinesRoutinesGet(),
    // Routine list is small + rarely changes — bump above the global
    // 30s default to keep refetch chatter down. Window-focus + reconnect
    // refetches still fire (inherited from the global default).
    staleTime: 5 * 60_000,
  });
}

/**
 * Manually fire a routine (`POST /routines/{id}/trigger` → 202 Accepted).
 *
 * The actual execution surfaces asynchronously via the WS
 * `routine_executed` frame; this mutation only confirms the gateway
 * accepted the trigger. On success the caller fires a toast and we
 * invalidate the list so the next render reflects any state the WS
 * bridge has already pushed.
 */
export function useTriggerRoutine(): UseMutationResult<
  RoutineTriggerResponse,
  Error,
  { id: string }
> {
  const queryClient = useQueryClient();
  return useMutation<RoutineTriggerResponse, Error, { id: string }>({
    mutationFn: ({ id }) =>
      RoutinesService.triggerRoutineRoutinesRoutineIdTriggerPost({ routineId: id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ROUTINES_QUERY_KEY });
    },
  });
}

/**
 * Delete a routine.
 *
 * The F-300 OpenAPI surface ships list + trigger at v0.1 (T-015 stubs
 * everything else as 501). We still call `DELETE /routines/{id}` via
 * the generated request core so the UI is ready when the endpoint
 * lands; in the meantime the mutation rejects and the caller surfaces
 * the "use HA advanced editor" fallback CTA.
 */
export function useDeleteRoutine(): UseMutationResult<void, Error, { id: string }> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      await gatewayRequest<void>(OpenAPI, {
        method: "DELETE",
        url: "/routines/{routine_id}",
        path: { routine_id: id },
        errors: {
          401: "Unauthorized",
          404: "Not Found",
          501: "Not Implemented",
          503: "Service Unavailable",
        },
      });
    },
    onSuccess: (_data, { id }) => {
      // Optimistically remove from the cached list so the row vanishes
      // before the refetch completes.
      const prev = queryClient.getQueryData<RoutineListResponse>(ROUTINES_QUERY_KEY);
      if (prev) {
        queryClient.setQueryData<RoutineListResponse>(ROUTINES_QUERY_KEY, {
          ...prev,
          routines: prev.routines.filter((r) => r.id !== id),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ROUTINES_QUERY_KEY });
    },
  });
}

/**
 * Toggle a routine's `active` flag.
 *
 * Same F-300 caveat as `useDeleteRoutine` — the PATCH endpoint isn't
 * exposed in the generated client yet, so we drive it through the
 * request core. On success we patch the cached list immediately so the
 * pill swap is instant; the invalidation refresh then reconciles with
 * the authoritative state.
 */
export function useToggleRoutine(): UseMutationResult<
  Routine,
  Error,
  { id: string; active: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation<Routine, Error, { id: string; active: boolean }>({
    mutationFn: async ({ id, active }) => {
      const response = await gatewayRequest<Routine>(OpenAPI, {
        method: "PATCH",
        url: "/routines/{routine_id}",
        path: { routine_id: id },
        body: { active },
        mediaType: "application/json",
        errors: {
          401: "Unauthorized",
          404: "Not Found",
          501: "Not Implemented",
          503: "Service Unavailable",
        },
      });
      return response;
    },
    onSuccess: (_data, { id, active }) => {
      const prev = queryClient.getQueryData<RoutineListResponse>(ROUTINES_QUERY_KEY);
      if (prev) {
        queryClient.setQueryData<RoutineListResponse>(ROUTINES_QUERY_KEY, {
          ...prev,
          routines: prev.routines.map((r) =>
            r.id === id ? { ...r, active } : r,
          ),
        });
      }
      void queryClient.invalidateQueries({ queryKey: ROUTINES_QUERY_KEY });
    },
  });
}

/**
 * Pure sort: active routines first, then alphabetical by name within
 * each bucket. Exported so tests + the page share the same ordering.
 */
export function sortRoutines(routines: ReadonlyArray<Routine>): Routine[] {
  return [...routines].sort((a, b) => {
    if (a.active !== b.active) {
      return a.active ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });
}
